import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { ProjectType } from "reedy-data/models";
import { getProjectsQuery, IProject } from "integrations/firebase/collections";
import { useApi } from "hooks/useApi";
import { PickerProject } from "./AddToProjectPicker.types";

interface UseProjectPickerDataArgs {
  organisationId?: string;
  userId?: string;
  productId?: string;
  /** Gate the Firestore listener so it only runs while the picker is open. */
  enabled: boolean;
}

interface ProjectPickerData {
  myProjects: PickerProject[];
  sharedProjects: PickerProject[];
  isLoading: boolean;
}

const ownerDisplay = (
  profile?: {
    firstName?: string | null;
    lastName?: string | null;
  } | null,
) => {
  if (!profile) {
    return { name: undefined, initials: undefined };
  }

  const name = `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim();
  const initials =
    `${profile.firstName?.[0] ?? ""}${profile.lastName?.[0] ?? ""}`.toUpperCase();

  return {
    name: name || undefined,
    initials: initials || undefined,
  };
};

const toPickerProject = (
  project: Partial<IProject>,
  userId: string | undefined,
  productId: string | undefined,
): PickerProject | null => {
  if (!project.id) {
    return null;
  }

  const productIds = project.productIds ?? [];
  const isShared = Boolean(userId && project.userId !== userId);

  return {
    id: project.id,
    name: project.name ?? "",
    isShared,
    assigned: Boolean(productId && productIds.includes(productId)),
    productIds,
    ownerId: isShared ? project.userId : undefined,
  };
};

export const useProjectPickerData = ({
  organisationId,
  userId,
  productId,
  enabled,
}: UseProjectPickerDataArgs): ProjectPickerData => {
  const api = useApi();
  const [allProjects = [], projectsLoading] = useCollectionData(
    organisationId && enabled
      ? getProjectsQuery(organisationId, undefined, {
          type: ProjectType.ISBNS,
        })
      : undefined,
  );

  const myProjects = useMemo(
    () =>
      allProjects
        .map((project) => toPickerProject(project, userId, productId))
        .filter((project): project is PickerProject =>
          Boolean(project && !project.isShared),
        ),
    [allProjects, userId, productId],
  );

  const baseSharedProjects = useMemo(
    () =>
      allProjects
        .map((project) => toPickerProject(project, userId, productId))
        .filter((project): project is PickerProject =>
          Boolean(project?.isShared),
        ),
    [allProjects, userId, productId],
  );

  // Shared-project owner info is not reliably stored on the project document,
  // so resolve each owner's display name through the users endpoint. The
  // `["users", id]` key matches the rest of the app, so results are shared.
  const ownerIds = useMemo(
    () =>
      Array.from(
        new Set(
          baseSharedProjects
            .map((project) => project.ownerId)
            .filter((id): id is string => Boolean(id)),
        ),
      ),
    [baseSharedProjects],
  );

  const ownerQueries = useQueries({
    queries: ownerIds.map((id) => ({
      queryKey: ["users", id],
      queryFn: async () => (await api.users.get(id)) ?? null,
      staleTime: Infinity,
      retry: false,
    })),
  });

  const ownersById = useMemo(
    () =>
      new Map(
        ownerIds.map((id, index) => [id, ownerQueries[index]?.data] as const),
      ),
    [ownerIds, ownerQueries],
  );

  const sharedProjects = useMemo(
    () =>
      baseSharedProjects.map((project) => {
        const profile = project.ownerId
          ? ownersById.get(project.ownerId)
          : undefined;
        const { name, initials } = ownerDisplay(profile);

        return { ...project, ownerName: name, ownerInitials: initials };
      }),
    [baseSharedProjects, ownersById],
  );

  return {
    myProjects,
    sharedProjects,
    isLoading: projectsLoading,
  };
};
